import { UNITS } from '../../contexts/InventoryContext';

/**
 * UnitSwitch — what each row IS (plan §3.4c): the inventory as uploaded (one row
 * per file) or as detected (one row per document found inside the files). Every
 * view mode, filter, sort, count and bulk action applies inside the chosen unit.
 *
 * Props:
 *   unit             — 'files' | 'documents' (resolved; never null here)
 *   counts           — { files, documents, cutFiles, children } from the full list
 *   showSourceFiles  — Documents unit: also list the files that were cut
 *   onChange(unit), onToggleSources(bool)
 */
export default function UnitSwitch({ unit, counts, showSourceFiles = false, onChange, onToggleSources }) {
  const c = counts || { files: 0, documents: 0, cutFiles: 0, children: 0 };
  const isDocs = unit === UNITS.DOCUMENTS;
  const filesWord = c.cutFiles === 1 ? 'file' : 'files';
  return (
    <div className="inv-unit-bar" data-testid="inv-unit-bar" data-unit={unit}>
      <div className="inv-unit-switch" role="tablist" aria-label="What each row is">
        <button
          type="button" role="tab" aria-selected={!isDocs}
          className={`inv-unit-btn${!isDocs ? ' inv-unit-btn--active' : ''}`}
          data-testid="inv-unit-files"
          title="One row per uploaded file — the inventory as it was uploaded"
          onClick={() => onChange?.(UNITS.FILES)}
        >
          <span className="inv-unit-label">Files</span>
          <span className="inv-unit-count">{c.files}</span>
        </button>
        <button
          type="button" role="tab" aria-selected={isDocs}
          className={`inv-unit-btn${isDocs ? ' inv-unit-btn--active' : ''}`}
          data-testid="inv-unit-documents"
          title="One row per document — a long file that holds several documents is shown as those documents"
          onClick={() => onChange?.(UNITS.DOCUMENTS)}
        >
          <span className="inv-unit-label">Documents</span>
          <span className="inv-unit-count">{c.documents}</span>
        </button>
      </div>
      <span className="inv-unit-caption" data-testid="inv-unit-caption">
        {isDocs
          ? (c.children > 0
              ? <><strong>{c.children}</strong> of these were found inside <strong>{c.cutFiles}</strong> {filesWord} · as detected</>
              : 'No file has been split into documents yet — every document is a file · as detected')
          : (c.cutFiles > 0
              ? <><strong>{c.cutFiles}</strong> {filesWord} {c.cutFiles === 1 ? 'holds' : 'hold'} <strong>{c.children}</strong> documents · as uploaded</>
              : 'As uploaded')}
      </span>
      {isDocs && c.cutFiles > 0 && (
        <label className="inv-unit-toggle" title="Also show the files that were split into documents, as rows of their own">
          <input type="checkbox" checked={!!showSourceFiles} onChange={(e) => onToggleSources?.(e.target.checked)} data-testid="gallery-show-sources" />
          <span>Show source files</span>
        </label>
      )}
    </div>
  );
}
